import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Loader2, ArrowRight, CheckCircle, Pencil, X, Check } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import LangToggle from '../components/onboarding/LangToggle';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// External CRM webhook — receives the completed funnel lead.
const CRM_WEBHOOK = 'https://n8n-bccq.srv1869685.hstgr.cloud/webhook/lead-to-crm';

// Maps the UI source label to the CRM's exact select options.
const QUELLE_MAP = {
  'Social Media':       'Social Media',
  'Press':              'Press',
  'Personal referral':  'Personal Referral',
  'Personal Referral':  'Personal Referral',
  'Other':              'Other',
};

// Maps our channel keys (which use "andere") to the CRM's expected keys.
const CHANNEL_KEY_MAP = {
  linkedin: 'linkedin', instagram: 'instagram', facebook: 'facebook',
  x: 'x', tiktok: 'tiktok', youtube: 'youtube', blog: 'blog', andere: 'other',
};

// Reads utm_source/medium/campaign/gclid from the URL query string and, if
// present, stashes them in sessionStorage so a later step can attach them
// to the CRM submission even after the visitor navigates between steps.
function captureUtmParams() {
  try {
    const url = new URL(window.location.href);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'gclid'];
    keys.forEach(k => {
      const v = url.searchParams.get(k);
      if (v) sessionStorage.setItem(`xch_${k}`, v);
    });
  } catch (_) { /* ignore — sessionStorage/URL may be unavailable in edge cases */ }
}
function readStoredUtm() {
  const out = {};
  try {
    ['utm_source', 'utm_medium', 'utm_campaign', 'gclid'].forEach(k => {
      const v = sessionStorage.getItem(`xch_${k}`);
      if (v) out[k] = v;
    });
  } catch (_) { /* noop */ }
  return out;
}

// Turns the (edited) analysis object into the CRM's expected "analyse" shape.
// Omits any key whose value is empty/missing so the payload stays clean.
function buildAnalysePayload(a) {
  if (!a || typeof a !== 'object') return {};
  const out = {};
  if (a.companyName) out.name = a.companyName;
  const descParts = [a.description, a.industry, a.coreActivities].filter(Boolean);
  if (descParts.length) out.description = descParts.join(' • ');
  const usps = (a.usps || [])
    .map(u => (typeof u === 'string' ? u : u?.name || u?.usp || ''))
    .filter(Boolean);
  if (usps.length) out.usps = usps;
  const ton = a.tonality || {};
  const langStyle = ton.style || a.brandVoice;
  if (langStyle) out.language_style = langStyle;
  const toneParts = [ton.addressing, ton.formality].filter(Boolean);
  const tone = toneParts.length ? toneParts.join(' • ') : a.tone;
  if (tone) out.tone = tone;
  if ((a.keywords || []).length) out.keywords = a.keywords;
  const audiences = (a.targetAudiences || [])
    .map(aud => {
      const item = {};
      if (aud?.name) item.name = aud.name;
      const audDesc = aud?.description || aud?.demographics;
      if (audDesc) item.description = audDesc;
      if ((aud?.painPoints || []).length) item.pain_points = aud.painPoints;
      return item;
    })
    .filter(item => Object.keys(item).length > 0);
  if (audiences.length) out.zielgruppen = audiences;
  return out;
}

// Converts the "filled channels" array from step 3 into the CRM dict shape.
function buildChannelsPayload(filledChannels) {
  const dict = {};
  (filledChannels || []).forEach(c => {
    const mappedKey = CHANNEL_KEY_MAP[c.platform] || c.platform;
    if (c.url && c.url.trim()) dict[mappedKey] = c.url.trim();
  });
  return dict;
}

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -20 },
};
const transition = { duration: 0.4, ease: [0.16, 1, 0.3, 1] };

const PLATFORMS = [
  { key: 'linkedin',   label: 'LinkedIn',       placeholder: 'https://linkedin.com/company/...' },
  { key: 'instagram',  label: 'Instagram',      placeholder: 'https://instagram.com/...' },
  { key: 'facebook',   label: 'Facebook',       placeholder: 'https://facebook.com/...' },
  { key: 'x',          label: 'X / Twitter',    placeholder: 'https://x.com/...' },
  { key: 'tiktok',     label: 'TikTok',         placeholder: 'https://tiktok.com/@...' },
  { key: 'youtube',    label: 'YouTube',        placeholder: 'https://youtube.com/@...' },
  { key: 'blog',       label: 'Blog / Website', placeholder: 'https://blog.example.com' },
  { key: 'andere',     label: 'Other',          placeholder: 'https://...' },
];

const SOURCE_OPTIONS = ['Social Media', 'Press', 'Personal referral', 'Other'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function StepDots({ current, total }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className="rounded-full transition-all duration-300"
          style={{
            width: i + 1 === current ? 24 : 8,
            height: 8,
            background: i + 1 <= current ? '#00D9C8' : '#CBD5E0',
          }}
        />
      ))}
    </div>
  );
}

function TealBadge({ children, variant = 'teal' }) {
  const styles = {
    teal:   { background: 'rgba(0,217,200,0.12)', color: '#00B5A8',  border: '1px solid rgba(0,217,200,0.3)' },
    yellow: { background: 'rgba(234,179,8,0.12)', color: '#B45309',  border: '1px solid rgba(234,179,8,0.3)' },
    grey:   { background: 'rgba(90,108,125,0.12)',color: '#5A6C7D',  border: '1px solid rgba(90,108,125,0.3)' },
    dark:   { background: '#0A1628',               color: '#00D9C8',  border: '1px solid rgba(0,217,200,0.3)' },
    blue:   { background: 'rgba(59,130,246,0.12)', color: '#2563EB', border: '1px solid rgba(59,130,246,0.3)' },
  };
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" style={styles[variant]}>
      {children}
    </span>
  );
}

function Card({ children }) {
  return (
    <div className="rounded-xl mb-4" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0' }}>
      {children}
    </div>
  );
}

function EditableField({ value, onSave, multiline = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value);
  const handleSave   = () => { onSave(draft); setEditing(false); };
  const handleCancel = () => { setDraft(value); setEditing(false); };

  if (!editing) {
    return (
      <span className="group inline-flex items-start gap-2">
        <span style={{ color: '#0A1628' }}>{value}</span>
        <button
          onClick={() => setEditing(true)}
          className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
          style={{ color: '#00D9C8' }}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  }
  return (
    <span className="flex items-start gap-2 w-full">
      {multiline ? (
        <textarea
          autoFocus value={draft} onChange={e => setDraft(e.target.value)} rows={3}
          className="flex-1 text-sm rounded-lg px-3 py-2 resize-none focus:outline-none"
          style={{ border: '1.5px solid #00D9C8', background: '#F7FAFC', color: '#0A1628' }}
        />
      ) : (
        <input
          autoFocus type="text" value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
          className="flex-1 text-sm rounded-lg px-3 py-1.5 focus:outline-none"
          style={{ border: '1.5px solid #00D9C8', background: '#F7FAFC', color: '#0A1628' }}
        />
      )}
      <button onClick={handleSave}   className="mt-1" style={{ color: '#00D9C8' }}><Check className="w-4 h-4" /></button>
      <button onClick={handleCancel} className="mt-1" style={{ color: '#5A6C7D' }}><X className="w-4 h-4" /></button>
    </span>
  );
}

function PrimaryButton({ onClick, disabled, children, testId }) {
  return (
    <button
      onClick={onClick} disabled={disabled}
      data-testid={testId}
      className="w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
      style={{ background: '#00D9C8', color: '#0A1628' }}
    >
      {children}
    </button>
  );
}

function BackButton({ onClick }) {
  return (
    <button onClick={onClick} className="w-full py-3 mt-2 text-sm" style={{ color: '#5A6C7D' }}>
      ← Back
    </button>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
      style={{ background: '#F7FAFC', border: '1.5px solid #E2E8F0', color: '#0A1628', '--tw-ring-color': '#00D9C8' }}
    />
  );
}

// ── STEP 1: Website ───────────────────────────────────────────────────────────
function StepWebsite({ sessionId, onComplete }) {
  const { lang } = useLanguage();
  const [url, setUrl]         = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleAnalyze = async () => {
    setError('');
    if (!url.trim()) return setError((lang === 'de' ? 'Bitte gib eine Website-URL ein.' : 'Please enter a website URL.'));
    const normalized = url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/onboarding/analyze-company`, {
        url: normalized, session_id: sessionId,
      });

      // Synchronous response
      if (res.data.analysis) {
        onComplete(res.data.analysis, normalized);
        return;
      }

      // Background job → poll
      if (res.data.job_id && res.data.status === 'processing') {
        const jobId = res.data.job_id;
        for (let i = 0; i < 90; i++) {
          await new Promise(r => setTimeout(r, 2000));
          try {
            const s = await axios.get(`${API}/onboarding/analyze-company/${jobId}`);
            if (s.data.status === 'completed' && s.data.analysis) {
              onComplete(s.data.analysis, normalized);
              return;
            }
            if (s.data.status === 'error') {
              setError(s.data.error || (lang === 'de' ? 'Analyse fehlgeschlagen.' : 'Analysis failed.'));
              return;
            }
          } catch (_) { /* transient — keep polling */ }
        }
        setError(lang === 'de' ? 'Analyse dauert zu lange. Bitte erneut versuchen.' : 'Analysis is taking too long. Please retry.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || (lang === 'de' ? 'Analyse fehlgeschlagen. Bitte erneut versuchen.' : 'Analysis failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="inline-block mb-6"
        >
          <Loader2 className="w-12 h-12" style={{ color: '#00D9C8' }} />
        </motion.div>
        <h2 className="text-2xl font-bold mb-3" style={{ color: '#0A1628' }}>
          {lang === 'de' ? 'Dein Unternehmen wird analysiert…' : 'Analysing your company…'}
        </h2>
        <p className="mb-6" style={{ color: '#5A6C7D' }}>{lang === 'de' ? 'Das dauert nur einen Moment.' : 'This will only take a moment.'}</p>
        <div className="space-y-3 text-left max-w-xs mx-auto">
          {[
            (lang === 'de' ? 'Website wird analysiert…' : 'Reading your website…'),
            (lang === 'de' ? 'Einzigartige Merkmale werden identifiziert…' : 'Identifying what makes your company unique…'),
            (lang === 'de' ? 'Zielgruppen & Positionierung werden erkannt…' : 'Detecting target audiences & positioning…'),
            (lang === 'de' ? 'Content-Strategie wird aufgebaut…' : 'Building content strategy…'),
          ].map((t, i) => (
            <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.7 }}
              className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#00D9C8' }} />
              <span className="text-sm" style={{ color: '#5A6C7D' }}>{t}</span>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-4xl font-black tracking-tight mb-3" style={{ color: '#0A1628' }}>
        {lang === 'de' ? 'Erzähl uns von deinem Unternehmen' : 'Tell us about your company'}
      </h2>
      <p className="mb-8" style={{ color: '#5A6C7D' }}>
        {lang === 'de' ? 'Wir analysieren deine Website und verstehen, was dein Unternehmen einzigartig macht.' : "We'll analyse your website and understand what makes your business unique."}
      </p>
      <div className="rounded-2xl p-6" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0' }}>
        <label className="block text-sm font-medium mb-2" style={{ color: '#5A6C7D' }}>{lang === 'de' ? 'Deine Website' : 'Your website'}</label>
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#5A6C7D' }} />
            <input
              type="url" value={url} onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
              placeholder={lang === 'de' ? 'https://deine-website.de' : 'https://your-website.com'}
              className="w-full h-12 pl-12 pr-4 rounded-xl text-base focus:outline-none focus:ring-2"
              style={{ background: '#F7FAFC', border: '1.5px solid #E2E8F0', color: '#0A1628' }}
            />
          </div>
          <button
            onClick={handleAnalyze} disabled={!url.trim()}
            className="h-12 px-5 rounded-xl font-semibold text-sm transition-opacity disabled:opacity-40"
            style={{ background: '#00D9C8', color: '#0A1628' }}
          >
            {lang === 'de' ? 'Analysieren →' : 'Analyse →'}
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
      </div>
    </div>
  );
}

// ── STEP 2: Analysis result ───────────────────────────────────────────────────
function StepAnalysisResult({ analysis: init, onComplete }) {
  const { lang } = useLanguage();
  const [a, setA] = useState(init);
  const upd  = (k, v)       => setA(prev => ({ ...prev, [k]: v }));
  const updN = (k, sk, v)   => setA(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [sk]: v } }));

  const audiences = a.targetAudiences || [];
  const goals     = a.marketingGoals  || [];
  const channels  = a.channelMix      || [];
  const themes    = a.contentThemes   || [];
  const ton       = a.tonality        || {};

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <CheckCircle className="w-6 h-6" style={{ color: '#00D9C8' }} />
        <h2 className="text-2xl font-bold tracking-tight" style={{ color: '#0A1628' }}>{lang === 'de' ? 'Analyse abgeschlossen' : 'Analysis complete'}</h2>
      </div>
      <p className="mb-5 text-sm" style={{ color: '#5A6C7D' }}>
        {lang === 'de' ? 'Hier ist deine Firmenanalyse. Du kannst alles bearbeiten — klick auf das ✏️-Symbol.' : "Here's your company analysis. You can edit anything — just click the ✏️ icon."}
      </p>

      <div className="max-h-[55vh] overflow-y-auto pr-1">

        {/* Company Overview */}
        <Card>
          <div className="p-5">
            <h3 className="text-lg font-bold mb-1">
              <EditableField value={a.companyName || 'Your company'} onSave={v => upd('companyName', v)} />
            </h3>
            <p className="text-sm mb-4" style={{ color: '#5A6C7D' }}>
              <EditableField
                value={`${a.industry || 'Industry'}${a.coreActivities ? ` • ${a.coreActivities}` : ''}`}
                onSave={v => upd('industry', v)}
              />
            </p>
            {(a.usps || []).length > 0 && (
              <div className="mb-4">
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#5A6C7D' }}>Unique selling points (USPs)</p>
                <div className="flex flex-wrap gap-2">
                  {(a.usps || []).slice(0, 4).map((usp, i) => (
                    <TealBadge key={i} variant="dark">{typeof usp === 'string' ? usp : usp?.name || 'USP'}</TealBadge>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#5A6C7D' }}>Language style</p>
                <EditableField value={ton.style || a.brandVoice || 'Professional'} onSave={v => updN('tonality', 'style', v)} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#5A6C7D' }}>Tone</p>
                <EditableField value={`${ton.addressing || 'You'} • ${ton.formality || a.tone || 'Formal'}`} onSave={v => updN('tonality', 'addressing', v)} />
              </div>
            </div>
            {(a.keywords || []).length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#5A6C7D' }}>Keywords</p>
                <div className="flex flex-wrap gap-1">
                  {(a.keywords || []).slice(0, 6).map((kw, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded" style={{ background: '#F0F4F8', color: '#5A6C7D' }}>{kw}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {audiences.length > 0 && (
          <Card>
            <div className="p-5">
              <p className="text-xs uppercase tracking-wider mb-3" style={{ color: '#5A6C7D' }}>{lang === 'de' ? 'Zielgruppen' : 'Target audiences'}</p>
              <div className="space-y-3">
                {audiences.slice(0, 2).map((aud, i) => (
                  <div key={i} className="p-3 rounded-lg" style={{ background: '#F7FAFC' }}>
                    <p className="text-sm font-medium mb-1" style={{ color: '#0A1628' }}>{aud.name || `Audience ${i + 1}`}</p>
                    <p className="text-xs" style={{ color: '#5A6C7D' }}>{aud.description || aud.demographics}</p>
                    {(aud.painPoints || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {aud.painPoints.slice(0, 2).map((p, j) => <span key={j} className="text-xs" style={{ color: '#E53E3E' }}>• {p}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {goals.length > 0 && (
          <Card>
            <div className="p-5">
              <p className="text-xs uppercase tracking-wider mb-3" style={{ color: '#5A6C7D' }}>{lang === 'de' ? 'Marketing-Ziele' : 'Marketing goals'}</p>
              <div className="space-y-2">
                {goals.slice(0, 3).map((g, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#0A1628' }}>{g.goal || g}</span>
                    {g.priority && (
                      <TealBadge variant={g.priority === 'hoch' ? 'teal' : g.priority === 'mittel' ? 'yellow' : 'grey'}>
                        {g.priority === 'hoch' ? 'high' : g.priority === 'mittel' ? 'medium' : 'low'}
                      </TealBadge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {channels.length > 0 && (
          <Card>
            <div className="p-5">
              <p className="text-xs uppercase tracking-wider mb-3" style={{ color: '#5A6C7D' }}>Recommended channel mix</p>
              <div className="space-y-2">
                {channels.slice(0, 4).map((ch, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ background: '#F7FAFC' }}>
                    <span className="text-sm" style={{ color: '#0A1628' }}>{ch.channel}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs" style={{ color: '#5A6C7D' }}>{ch.frequencyPerWeek}x/week</span>
                      <TealBadge variant={ch.priority === 'primär' ? 'teal' : ch.priority === 'sekundär' ? 'blue' : 'grey'}>
                        {ch.priority === 'primär' ? 'primary' : ch.priority === 'sekundär' ? 'secondary' : ch.priority}
                      </TealBadge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {themes.length > 0 && (
          <Card>
            <div className="p-5">
              <p className="text-xs uppercase tracking-wider mb-3" style={{ color: '#5A6C7D' }}>{lang === 'de' ? 'Content-Themen' : 'Content themes'}</p>
              <div className="space-y-2">
                {themes.slice(0, 3).map((t, i) => (
                  <div key={i}>
                    <p className="text-sm font-medium" style={{ color: '#0A1628' }}>{t.mainTheme || t}</p>
                    {(t.subTopics || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {t.subTopics.slice(0, 3).map((s, j) => <span key={j} className="text-xs" style={{ color: '#5A6C7D' }}>• {s}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>

      <p className="text-xs mt-3 mb-4" style={{ color: '#5A6C7D' }}>
        This information will be used to keep all your content on-brand.
      </p>
      <PrimaryButton onClick={() => onComplete(a)}>
        Looks good — continue <ArrowRight className="w-4 h-4" />
      </PrimaryButton>
    </div>
  );
}

// ── STEP 3: Channels ──────────────────────────────────────────────────────────
function StepChannels({ onComplete, onBack }) {
  const { lang } = useLanguage();
  const [channels, setChannels] = useState({});
  const [error, setError]       = useState('');
  const filled = PLATFORMS.filter(p => channels[p.key]?.trim());

  const handleContinue = () => {
    if (filled.length < 1) return setError('Please add at least one channel.');
    setError('');
    onComplete(filled.map(p => ({ platform: p.key, url: channels[p.key].trim() })));
  };

  return (
    <div>
      <h2 className="text-3xl font-black tracking-tight mb-2" style={{ color: '#0A1628' }}>Your communication channels</h2>
      <p className="mb-6 text-sm" style={{ color: '#5A6C7D' }}>Which platforms are you active on? At least 1, up to 8.</p>
      <div className="rounded-2xl overflow-hidden mb-4" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0' }}>
        {PLATFORMS.map((p, i) => (
          <div key={p.key} className="px-5 py-4" style={{ borderBottom: i < PLATFORMS.length - 1 ? '1px solid #E2E8F0' : 'none' }}>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#5A6C7D' }}>{p.label}</label>
            <input
              type="url" value={channels[p.key] || ''}
              onChange={e => setChannels(prev => ({ ...prev, [p.key]: e.target.value }))}
              placeholder={p.placeholder}
              className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
              style={{ background: '#F7FAFC', border: '1.5px solid #E2E8F0', color: '#0A1628' }}
            />
          </div>
        ))}
      </div>
      {filled.length > 0 && (
        <p className="text-center text-xs mb-3" style={{ color: '#00B5A8' }}>
          {filled.length} channel{filled.length > 1 ? 's' : ''} added
        </p>
      )}
      {error && <p className="text-red-500 text-sm text-center mb-3">{error}</p>}
      <PrimaryButton onClick={handleContinue}>Continue →</PrimaryButton>
      <BackButton onClick={onBack} />
    </div>
  );
}

// ── STEP 4: Personal data ─────────────────────────────────────────────────────
function StepPersonalData({ sessionId, channels, onComplete, onBack }) {
  const { lang } = useLanguage();
  const [form, setForm]       = useState({ firstName: '', lastName: '', email: '', source: '', sourceOther: '', companyWebsiteHp: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    if (!form.firstName.trim()) return setError(lang === 'de' ? 'Vorname ist erforderlich.' : 'First name is required.');
    if (!form.lastName.trim())  return setError(lang === 'de' ? 'Nachname ist erforderlich.' : 'Last name is required.');
    if (!form.email.trim() || !form.email.includes('@')) return setError(lang === 'de' ? 'Bitte eine gültige E-Mail-Adresse angeben.' : 'A valid email address is required.');
    setError('');

    // Honeypot: if the invisible field is filled it's almost certainly a bot.
    // Skip the verification email + CRM submission entirely and jump straight
    // to the success state so the bot gets no useful signal about the trap.
    if (form.companyWebsiteHp.trim()) {
      onComplete({ ...form, honeypotTripped: true });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/demo/send-verification`, {
        session_id: sessionId,
        first_name: form.firstName.trim(), last_name: form.lastName.trim(),
        email: form.email.trim(), channels,
        source: form.source || null, source_other: form.sourceOther || null,
      });
      onComplete({ ...form, honeypotTripped: false });
    } catch (e) {
      setError(e.response?.data?.detail || (lang === 'de' ? 'Etwas ist schiefgelaufen. Bitte erneut versuchen.' : 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-3xl font-black tracking-tight mb-2" style={{ color: '#0A1628' }}>Almost there!</h2>
      <p className="mb-6 text-sm" style={{ color: '#5A6C7D' }}>We need a few more details to prepare your demo.</p>
      <div className="rounded-2xl overflow-hidden mb-4" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0' }}>
        <div className="grid grid-cols-2 gap-4 p-5" style={{ borderBottom: '1px solid #E2E8F0' }}>
          {[['firstName', 'First name', 'Jane'], ['lastName', 'Last name', 'Smith']].map(([k, label, ph]) => (
            <div key={k}>
              <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: '#5A6C7D' }}>{label} *</label>
              <TextInput value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph} />
            </div>
          ))}
        </div>
        <div className="px-5 py-4" style={{ borderBottom: '1px solid #E2E8F0' }}>
          <label className="block text-xs uppercase tracking-wider mb-1.5" style={{ color: '#5A6C7D' }}>Email address *</label>
          <TextInput type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@company.com" />
        </div>
        <div className="px-5 py-4">
          <label className="block text-xs uppercase tracking-wider mb-2" style={{ color: '#5A6C7D' }}>
            How did you hear about xchannels?{' '}
            <span className="normal-case" style={{ color: '#CBD5E0' }}>(optional)</span>
          </label>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {SOURCE_OPTIONS.map(opt => (
              <button key={opt} onClick={() => set('source', opt)}
                className="py-2 px-3 rounded-lg text-sm text-left transition-all"
                style={{
                  border: form.source === opt ? '1.5px solid #00D9C8' : '1.5px solid #E2E8F0',
                  background: form.source === opt ? 'rgba(0,217,200,0.08)' : '#F7FAFC',
                  color: form.source === opt ? '#00B5A8' : '#5A6C7D',
                  fontWeight: form.source === opt ? 600 : 400,
                }}>
                {opt}
              </button>
            ))}
          </div>
          {form.source === 'Other' && (
            <TextInput value={form.sourceOther} onChange={e => set('sourceOther', e.target.value)} placeholder="Where exactly?" />
          )}

          {/* Honeypot — invisible to real users, catches naive bots that
              blindly fill every field. If this ends up with a value we
              short-circuit and don't hit the CRM. */}
          <input
            type="text"
            name="company_website_hp"
            tabIndex={-1}
            autoComplete="off"
            value={form.companyWebsiteHp}
            onChange={e => set('companyWebsiteHp', e.target.value)}
            aria-hidden="true"
            data-testid="funnel-honeypot-hp"
            style={{
              position: 'absolute',
              left: '-10000px',
              top: 'auto',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
              opacity: 0,
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>
      {error && <p className="text-red-500 text-sm text-center mb-3">{error}</p>}
      <PrimaryButton onClick={handleSubmit} disabled={loading} testId="funnel-step4-submit">
        {loading ? (lang === 'de' ? 'Code wird gesendet…' : 'Sending code…') : (lang === 'de' ? 'Bestätigungscode senden →' : 'Send verification code →')}
      </PrimaryButton>
      <BackButton onClick={onBack} />
    </div>
  );
}

// ── STEP 5: Verification ──────────────────────────────────────────────────────
function StepVerification({ sessionId, email, onComplete, submitCrm }) {
  const { lang } = useLanguage();
  const [code, setCode]         = useState(['', '', '', '', '', '']);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [cooldown, setCooldown] = useState(0);
  const refList = [];

  const handleChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...code]; next[i] = val.slice(-1); setCode(next);
    if (val && i < 5) refList[i + 1]?.focus();
  };
  const handleKeyDown = (i, e) => { if (e.key === 'Backspace' && !code[i] && i > 0) refList[i - 1]?.focus(); };
  const handlePaste = e => {
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (p.length === 6) { setCode(p.split('')); refList[5]?.focus(); }
  };

  const handleVerify = async () => {
    const full = code.join('');
    if (full.length < 6) return setError(lang === 'de' ? 'Bitte alle 6 Ziffern eingeben.' : 'Please enter all 6 digits.');
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/demo/verify-email`, { session_id: sessionId, code: full });
    } catch (e) {
      setError(e.response?.data?.detail || (lang === 'de' ? 'Ungültiger Code. Bitte erneut versuchen.' : 'Invalid code. Please try again.'));
      setCode(['', '', '', '', '', '']); refList[0]?.focus();
      setLoading(false);
      return;
    }
    // Email verified → now push the collected lead to the CRM. If that fails
    // we keep the user on this step (all their data is intact upstream) and
    // let them retry the verify button which will re-attempt the CRM POST.
    try {
      if (submitCrm) await submitCrm();
      onComplete();
    } catch (e) {
      setError(
        lang === 'de'
          ? 'Das hat leider nicht geklappt — schreiben Sie uns direkt an office@xchannels.ai'
          : 'Something went wrong — please email us directly at office@xchannels.ai'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await axios.post(`${API}/demo/resend-verification`, { session_id: sessionId });
      setCooldown(60);
      const t = setInterval(() => setCooldown(c => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; }), 1000);
    } catch { setError('Something went wrong. Please try again.'); }
  };

  return (
    <div>
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
          style={{ background: 'rgba(0,217,200,0.1)' }}>
          <svg className="w-7 h-7" style={{ color: '#00D9C8' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#0A1628' }}>Verify your email</h2>
        <p className="text-sm" style={{ color: '#5A6C7D' }}>
          We sent a 6-digit code to <span style={{ color: '#0A1628', fontWeight: 600 }}>{email}</span>.
        </p>
      </div>
      <div className="rounded-2xl p-6 mb-4" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0' }}>
        <div className="flex gap-2 justify-center mb-5" onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input key={i} ref={el => { refList[i] = el; }}
              type="text" inputMode="numeric" maxLength={1} value={digit}
              onChange={e => handleChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)}
              className="w-12 h-14 text-center text-2xl font-bold rounded-xl focus:outline-none transition-all"
              style={{ border: digit ? '2px solid #00D9C8' : '2px solid #E2E8F0', background: '#F7FAFC', color: '#0A1628' }}
            />
          ))}
        </div>
        {error && <p className="text-red-500 text-sm text-center mb-4" data-testid="funnel-verify-error">{error}</p>}
        <PrimaryButton onClick={handleVerify} disabled={loading || code.join('').length < 6} testId="funnel-verify-submit">
          {loading ? (lang === 'de' ? 'Wird bestätigt…' : 'Verifying…') : (lang === 'de' ? 'E-Mail bestätigen →' : 'Verify email →')}
        </PrimaryButton>
      </div>
      <p className="text-center text-sm" style={{ color: '#5A6C7D' }}>
        Didn't receive a code?{' '}
        {cooldown > 0
          ? <span style={{ color: '#CBD5E0' }}>Resend in {cooldown}s</span>
          : <button onClick={handleResend} style={{ color: '#00D9C8' }} className="hover:underline">Resend</button>
        }
      </p>
    </div>
  );
}

// ── STEP 6: Success ───────────────────────────────────────────────────────────
function StepSuccess() {
  const { lang } = useLanguage();
  return (
    <div className="text-center">
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', duration: 0.6 }}
        className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6"
        style={{ background: 'rgba(0,217,200,0.1)' }}
      >
        <CheckCircle className="w-10 h-10" style={{ color: '#00D9C8' }} />
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <h1 className="text-2xl font-bold mb-4" style={{ color: '#0A1628' }}>
          Your free xchannels demo is being set up 🚀
        </h1>
        <p className="text-base leading-relaxed mb-8" style={{ color: '#5A6C7D' }}>
          We'll get back to you shortly once everything is ready. You'll receive a confirmation by email.
        </p>
        <div className="rounded-2xl p-5 text-left" style={{ background: '#FFFFFF', border: '1px solid #E2E8F0' }}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: 'rgba(0,217,200,0.1)' }}>
              <svg className="w-4 h-4" style={{ color: '#00D9C8' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold mb-1" style={{ color: '#0A1628' }}>What happens next?</p>
              <p className="text-sm leading-relaxed" style={{ color: '#5A6C7D' }}>
                Our team will set up your personal xchannels.ai demo. You'll then receive a direct access link by email.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const DemoOnboarding = () => {
  const { lang } = useLanguage();
  const [step, setStep]                = useState(1);
  const [sessionId]                    = useState(() => `demo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [companyAnalysis, setAnalysis] = useState(null);
  const [websiteUrl, setWebsiteUrl]    = useState('');
  const [channels, setChannels]        = useState([]);
  const [personalData, setPersonalData] = useState(null); // Full form (name, email, source, sourceOther, honeypot flag)

  // Capture UTM + gclid on first mount so the CRM lead knows the traffic source.
  useEffect(() => { captureUtmParams(); }, []);

  // Constructs the exact CRM payload — used both from step 5 (after verify)
  // and (optionally) from a retry hook. Reads the freshest edited analysis
  // and only the filled channels/source; UTMs come from sessionStorage.
  const buildCrmPayload = () => {
    const payload = {
      website: websiteUrl,
      analyse: buildAnalysePayload(companyAnalysis),
      channels: buildChannelsPayload(channels),
      vorname: personalData?.firstName?.trim() || '',
      nachname: personalData?.lastName?.trim() || '',
      email: personalData?.email?.trim() || '',
    };
    // "quelle" — only if visitor picked one AND it maps to a valid CRM option.
    const mappedSource = personalData?.source ? QUELLE_MAP[personalData.source] : null;
    if (mappedSource) payload.quelle = mappedSource;
    Object.assign(payload, readStoredUtm());
    return payload;
  };

  // Fires the actual POST. Returns nothing on success; throws on any
  // non-2xx / network error so StepVerification can surface it.
  const submitCrm = async () => {
    // Honeypot short-circuit: pretend everything worked, don't touch the CRM.
    if (personalData?.honeypotTripped) return;
    const res = await fetch(CRM_WEBHOOK, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildCrmPayload()),
    });
    if (!res.ok) throw new Error(`CRM webhook returned ${res.status}`);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F0F4F8' }}>
      <header className="w-full px-6 py-4" style={{ borderBottom: '1px solid #E2E8F0', background: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img
            src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png"
            alt="xchannels.ai"
            className="h-10"
            style={{ filter: 'brightness(0) saturate(100%) invert(8%) sepia(20%) saturate(2000%) hue-rotate(185deg)' }}
          />
          <div className="flex items-center gap-4">
            <LangToggle />
            <a href="/login" className="text-sm" style={{ color: '#5A6C7D' }}>
              {lang === 'de' ? 'Bereits registriert?' : 'Already registered?'}{' '}
              <span style={{ color: '#00B5A8', fontWeight: 600 }}>
                {lang === 'de' ? 'Anmelden' : 'Sign in'}
              </span>
            </a>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl">
          {step < 6 && <StepDots current={step} total={5} />}

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <StepWebsite sessionId={sessionId} onComplete={(analysis, url) => { setAnalysis(analysis); setWebsiteUrl(url); setStep(2); }} />
              </motion.div>
            )}
            {step === 2 && (
              <motion.div key="s2" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <StepAnalysisResult analysis={companyAnalysis} onComplete={updated => { setAnalysis(updated); setStep(3); }} />
              </motion.div>
            )}
            {step === 3 && (
              <motion.div key="s3" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <StepChannels onComplete={filled => { setChannels(filled); setStep(4); }} onBack={() => setStep(2)} />
              </motion.div>
            )}
            {step === 4 && (
              <motion.div key="s4" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <StepPersonalData
                  sessionId={sessionId} channels={channels}
                  onComplete={(fullForm) => {
                    setPersonalData(fullForm);
                    // Honeypot tripped → skip verification (bot won't have real
                    // email anyway) and land directly on the success screen
                    // without any external calls.
                    if (fullForm.honeypotTripped) setStep(6);
                    else setStep(5);
                  }}
                  onBack={() => setStep(3)}
                />
              </motion.div>
            )}
            {step === 5 && (
              <motion.div key="s5" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <StepVerification
                  sessionId={sessionId}
                  email={personalData?.email || ''}
                  submitCrm={submitCrm}
                  onComplete={() => setStep(6)}
                />
              </motion.div>
            )}
            {step === 6 && (
              <motion.div key="s6" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <StepSuccess firstName={personalData?.firstName || ''} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default DemoOnboarding;
